import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const STORAGE_KEY = "app_auth_state";

const defaultAuthState = {
  accessToken: null,
  expiresAtMs: null,
  roleType: null,
  user: null,
};

const AuthContext = createContext({
  accessToken: null,
  roleType: null,
  user: null,
  isAuthenticated: false,
  login: async () => {},
  logout: () => {},
  apiFetch: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : defaultAuthState;
    } catch (e) {
      return defaultAuthState;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  // Cross-tab sync: respond to changes in auth state written by other tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const next = JSON.parse(e.newValue);
          setState(next || defaultAuthState);
        } catch (_) {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const isAuthenticated = !!state?.accessToken;


  console.log("ENV:", process.env);

  const rawBaseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!rawBaseUrl) {
    console.warn("REACT_APP_API_BASE_URL is not defined!");
  }

  const baseUrl = (rawBaseUrl || "").replace(/\/$/, "");

  console.log("Base URL: ", baseUrl);

  const logout = useCallback(async () => {
    try {
      if (state.accessToken) {
        await fetch(`${baseUrl}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${state.accessToken}` },
        });
      }
    } catch (_) {}
    setState(defaultAuthState);
  }, [state.accessToken, baseUrl]);


  const login = useCallback(async ({ username, password }) => {
    const url = `${baseUrl}/auth/login`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const message = await res.text();
      const error = new Error(message || "Login failed");
      error.status = res.status;
      throw error;
    }
    const json = await res.json();
    const meRes = await fetch(`${baseUrl}/auth/me`, { headers: { Authorization: `Bearer ${json.access_token}` } });
    const me = meRes.ok ? await meRes.json() : { user_uuid: null, user_id: null, role_type: null, username };
    const expiresAtMs = Date.now() + (json?.expires_in || 0) * 1000;
    const next = {
      accessToken: json?.access_token || null,
      expiresAtMs,
      roleType: me?.role_type || null,
      user: { username: me?.username || username, user_uuid: me?.user_uuid, user_id: me?.user_id },
    };
    setState(next);
    return next;
  }, [baseUrl]);

  const apiFetch = useCallback(async (path, options = {}) => {
    const doFetch = async (token) =>
      fetch(`${baseUrl}${path}`, {
        ...options,
        headers: { ...(options.headers || {}), Authorization: token ? `Bearer ${token}` : undefined },
      });

    let res = await doFetch(state.accessToken);
    if (res.status === 401) {
      await logout();
      try { alert('Session expired. Please log in again.'); } catch (_) {}
      try { window.location.hash = '#/login'; } catch (_) {}
      throw new Error('Session expired');
    }
    return res;
  }, [baseUrl, state.accessToken, logout]);

  const value = useMemo(
    () => ({
      accessToken: state.accessToken,
      roleType: state.roleType,
      user: state.user,
      isAuthenticated,
      login,
      logout,
      apiFetch,
    }),
    [state, isAuthenticated, login, logout, apiFetch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);


